import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { UserType, SubAccountType, SubAccountListData } from '../types/user';
import { MenuItem } from './type';
import {
  createEncryptedStorage,
  getStorageEncryptionKey,
} from '../utils/encryptedStorage';
import { CacheEnum } from '../enum/cache';
import { getUserDetail, getSubAccountList } from '../api/user';
import { ApiResponse } from '../utils/request/interceptors';
import useStore from './index';

interface UserStoreState {
  userInfo: UserType | undefined;
  setUserInfo: (userInfo: UserType) => void;
  menuList: MenuItem[];
  setMenuList: (menuList: MenuItem[]) => void;
  subAccountList: SubAccountType[];
  setSubAccountList: (subAccountList: SubAccountType[]) => void;
  // 业务逻辑函数
  fetchUserDetails: (
    userId: string,
    country?: string,
    showErrorFn?: (messageKey: string) => void
  ) => Promise<boolean>;
  fetchSubAccountList: (
    showErrorFn?: (messageKey: string) => void
  ) => Promise<void>;
}

export const useUserStore = create<UserStoreState>()(
  persist(
    (set, get) => ({
      userInfo: undefined,
      setUserInfo: (info: UserType) => set(() => ({ userInfo: info })),
      menuList: [],
      setMenuList: (list: any[]) => set(() => ({ menuList: list })),
      subAccountList: [],
      setSubAccountList: (list: SubAccountType[]) =>
        set(() => ({ subAccountList: list })),

      // 获取用户详细信息
      fetchUserDetails: async (
        userId: string,
        country?: string,
        showErrorFn?: (messageKey: string) => void
      ): Promise<boolean> => {
        const { setUserInfo, setMenuList } = get();
        const { setLoginData } = useStore.getState(); // 添加这行
        const showError = showErrorFn || ((msg: string) => console.error(msg));

        try {
          let targetCountry = country;

          // 如果没有传入国家参数，获取基础用户信息来获取国家
          if (!targetCountry) {
            if (process.env.NODE_ENV === 'development') {
              console.log('获取基础用户信息...');
            }
            const basicUserInfo = await getUserDetail(Number(userId));
            if (process.env.NODE_ENV === 'development') {
              console.log('基础用户信息响应:', basicUserInfo);
            }

            if (basicUserInfo.msg !== 'success') {
              showError('web.login.getUserInfoFailed');
              return false;
            }

            // 解析用户国家信息
            targetCountry = basicUserInfo?.data?.country?.split(',')?.[0];
            if (process.env.NODE_ENV === 'development') {
              console.log('解析的国家信息:', targetCountry);
            }
          }

          setLoginData({
            country: targetCountry,
            id: userId,
          });

          if (!targetCountry) {
            console.error('无法获取用户国家信息');
            showError('web.login.getUserInfoFailed');
            return false;
          }

          // 根据国家获取详细用户信息
          const detailedUserInfo = await getUserDetail(
            Number(userId),
            targetCountry
          );

          if (detailedUserInfo.msg !== 'success') {
            console.error('获取详细用户信息失败:', detailedUserInfo);
            showError('web.login.getUserInfoFailed');
            return false;
          }

          // 保存菜单数据和用户信息到 store
          const { menus, ...userDataWithoutMenus } = detailedUserInfo.data;

          setMenuList(menus || []);
          setUserInfo(userDataWithoutMenus);

          return true;
        } catch (error) {
          console.error('获取用户详情失败:', error);
          showError('web.login.getUserInfoFailed');
          return false;
        }
      },

      // 获取子账号列表
      fetchSubAccountList: async (
        showErrorFn?: (messageKey: string) => void
      ): Promise<void> => {
        const { setSubAccountList } = get();
        const showError = showErrorFn || ((msg: string) => console.error(msg));
        try {
          const subAccountResponse =
            (await getSubAccountList()) as ApiResponse<SubAccountListData>;
          if (subAccountResponse.msg === 'success') {
            setSubAccountList(subAccountResponse?.data?.rows || []);
          } else {
            showError('web.login.getUserInfoFailed');
          }
        } catch (error) {
          console.error('获取子账号列表失败:', error);
          showError('web.login.getUserInfoFailed');
        }
      },
    }),
    {
      name: CacheEnum.userInfoStorage,
      // storage: createJSONStorage(() => localStorage),
      storage: createJSONStorage(() =>
        createEncryptedStorage(getStorageEncryptionKey())
      ),
    }
  )
);

export default useUserStore;
